import React, { createContext, useContext, useState } from 'react'
import type { ReactNode } from 'react'

interface Contrato {
  id: string
  numero_contrato: string
  tipo_contrato: 'principal' | 'adicional' | 'renovacao' | 'upgrade' | 'projeto' | 'aditivo'
  status_relacionamento: 'independente' | 'vinculado' | 'substitui' | 'complementa'
  plano_ids: string[]
  conteudo_original: string
  conteudo_editado: string
  dados_variaveis: any
  data_inicio?: string
  data_fim?: string
  status: string
  observacoes?: string
  arquivo_url?: string
  created_at: string
  updated_at: string
  contrato_pai?: {
    numero_contrato: string
    tipo_contrato: string
  }
  contrato_substitui?: {
    numero_contrato: string
    tipo_contrato: string
  }
}

interface Plano {
  id: string
  nome: string
  valor_base: number
  status: string
}

interface ContractModalContextType {
  isModalOpen: boolean
  clienteId: string | null
  planos: Plano[]
  openModal: (clienteId: string, planos: Plano[]) => void
  closeModal: () => void
  onContractCreated?: (contrato: Contrato) => void
  onEditContract?: (contrato: Contrato) => void
  setCallbacks: (callbacks: {
    onContractCreated: (contrato: Contrato) => void
    onEditContract: (contrato: Contrato) => void
  }) => void
  // Editor state
  isEditorOpen: boolean
  selectedContract: Contrato | null
  openEditor: (contrato: Contrato) => void
  closeEditor: () => void
}

const ContractModalContext = createContext<ContractModalContextType | undefined>(undefined)

export const useContractModal = () => {
  const context = useContext(ContractModalContext)
  if (!context) {
    throw new Error('useContractModal must be used within a ContractModalProvider')
  }
  return context
}

interface ContractModalProviderProps {
  children: ReactNode
}

export const ContractModalProvider: React.FC<ContractModalProviderProps> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [clienteId, setClienteId] = useState<string | null>(null)
  const [planos, setPlanos] = useState<Plano[]>([])
  const [onContractCreated, setOnContractCreated] = useState<((contrato: Contrato) => void) | undefined>()
  const [onEditContract, setOnEditContract] = useState<((contrato: Contrato) => void) | undefined>()
  
  // Editor state
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [selectedContract, setSelectedContract] = useState<Contrato | null>(null)

  const openModal = (newClienteId: string, newPlanos: Plano[]) => {
    setClienteId(newClienteId)
    setPlanos(newPlanos)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setClienteId(null)
    setPlanos([])
  }

  const openEditor = (contrato: Contrato) => {
    setSelectedContract(contrato)
    setIsEditorOpen(true)
  }

  const closeEditor = () => {
    setIsEditorOpen(false)
    setSelectedContract(null)
  }

  const setCallbacks = (callbacks: {
    onContractCreated: (contrato: Contrato) => void
    onEditContract: (contrato: Contrato) => void
  }) => {
    setOnContractCreated(() => callbacks.onContractCreated)
    setOnEditContract(() => callbacks.onEditContract)
  }

  const value: ContractModalContextType = {
    isModalOpen,
    clienteId,
    planos,
    openModal,
    closeModal,
    onContractCreated,
    onEditContract,
    setCallbacks,
    // Editor state
    isEditorOpen,
    selectedContract,
    openEditor,
    closeEditor
  }

  return (
    <ContractModalContext.Provider value={value}>
      {children}
    </ContractModalContext.Provider>
  )
}
